import { createTheme } from '@mui/material'
import { paletteOptions } from './themeOptions/palette'
import { borderOptions } from './themeOptions/borders'
import { spacingOptions } from './themeOptions/spacing'
import { typographyOptions } from './themeOptions/typography'
import { customComponentOptions } from './themeOptions/customComponents'

declare module '@mui/material/styles' {
  interface Palette {
    'transparency-0': string
    'transparency-100': string
    'transparency-200': string
    'transparency-300': string
    'transparency-400': string
    'transparency-500': string
    'transparency-600': string
    'transparency-700': string
    'transparency-800': string
    'transparency-900': string

    // Base
    'base-white': string
    'base-black': string

    // Sage
    'sage-50': string
    'sage-100': string
    'sage-200': string
    'sage-300': string
    'sage-400': string
    'sage-500': string
    'sage-600': string
    'sage-700': string
    'sage-800': string
    'sage-900': string

    // Accent
    'accent-100': string
    'accent-200': string
    'accent-300': string
    'accent-400': string
    'accent-500': string
    'accent-600': string
    'accent-700': string
    'accent-800': string
    'accent-900': string

    // Green
    'green-100': string
    'green-200': string
    'green-300': string
    'green-400': string
    'green-500': string
    'green-600': string
    'green-700': string
    'green-800': string
    'green-900': string

    // Orange
    'orange-100': string
    'orange-200': string
    'orange-300': string
    'orange-400': string
    'orange-500': string
    'orange-600': string
    'orange-700': string
    'orange-800': string
    'orange-900': string

    // Red
    'red-100': string
    'red-200': string
    'red-300': string
    'red-400': string
    'red-500': string
    'red-600': string
    'red-700': string
    'red-800': string
    'red-900': string
  }

  interface PaletteOptions {
    'transparency-0'?: string
    'transparency-100'?: string
    'transparency-200'?: string
    'transparency-300'?: string
    'transparency-400'?: string
    'transparency-500'?: string
    'transparency-600'?: string
    'transparency-700'?: string
    'transparency-800'?: string
    'transparency-900'?: string

    'base-white'?: string
    'base-black'?: string

    'sage-50'?: string
    'sage-100'?: string
    'sage-200'?: string
    'sage-300'?: string
    'sage-400'?: string
    'sage-500'?: string
    'sage-600'?: string
    'sage-700'?: string
    'sage-800'?: string
    'sage-900'?: string

    'accent-100'?: string
    'accent-200'?: string
    'accent-300'?: string
    'accent-400'?: string
    'accent-500'?: string
    'accent-600'?: string
    'accent-700'?: string
    'accent-800'?: string
    'accent-900'?: string

    'green-100'?: string
    'green-200'?: string
    'green-300'?: string
    'green-400'?: string
    'green-500'?: string
    'green-600'?: string
    'green-700'?: string
    'green-800'?: string
    'green-900'?: string

    'orange-100'?: string
    'orange-200'?: string
    'orange-300'?: string
    'orange-400'?: string
    'orange-500'?: string
    'orange-600'?: string
    'orange-700'?: string
    'orange-800'?: string
    'orange-900'?: string

    'red-100'?: string
    'red-200'?: string
    'red-300'?: string
    'red-400'?: string
    'red-500'?: string
    'red-600'?: string
    'red-700'?: string
    'red-800'?: string
    'red-900'?: string
  }
  interface Theme {
    borders: {
      'rounded-6': string
      'rounded-8': string
      'rounded-10': string
      'rounded-12': string
      'rounded-14': string
      'rounded-16': string
      'rounded-18': string
      'rounded-9999': string

      'width-1': string
      'width-1.5': string
      'width-2': string
    }
    space: {
      '4': string
      '8': string
      '12': string
      '16': string
      '20': string
      '24': string
      '28': string
      '32': string
      '40': string
      '48': string
      '64': string
      '80': string
      '96': string
      '128': string
      '160': string
    }
  }
  interface ThemeOptions {
    borders?: Theme['borders']
    space?: Theme['space']
  }
  interface TypographyVariants {
    'body-1': React.CSSProperties
    'body-2': React.CSSProperties
    'body-3': React.CSSProperties
    'body-4': React.CSSProperties
    'body-5': React.CSSProperties
    'body-6': React.CSSProperties
    'body-7': React.CSSProperties
    'body-8': React.CSSProperties
    'body-9': React.CSSProperties
    'body-10': React.CSSProperties
    'body-11': React.CSSProperties
    'body-12': React.CSSProperties
    'heading-1': React.CSSProperties
    'heading-2': React.CSSProperties
    'heading-3': React.CSSProperties
    'heading-4': React.CSSProperties
    'heading-5': React.CSSProperties
    'heading-6': React.CSSProperties
    'heading-7': React.CSSProperties
    'heading-8': React.CSSProperties
    'ui-1': React.CSSProperties
    'ui-2': React.CSSProperties
    'ui-3': React.CSSProperties
  }

  interface TypographyVariantsOptions {
    'body-1'?: React.CSSProperties
    'body-2'?: React.CSSProperties
    'body-3'?: React.CSSProperties
    'body-4'?: React.CSSProperties
    'body-5'?: React.CSSProperties
    'body-6'?: React.CSSProperties
    'body-7'?: React.CSSProperties
    'body-8'?: React.CSSProperties
    'body-9'?: React.CSSProperties
    'body-10'?: React.CSSProperties
    'body-11'?: React.CSSProperties
    'body-12'?: React.CSSProperties
    'heading-1'?: React.CSSProperties
    'heading-2'?: React.CSSProperties
    'heading-3'?: React.CSSProperties
    'heading-4'?: React.CSSProperties
    'heading-5'?: React.CSSProperties
    'heading-6'?: React.CSSProperties
    'heading-7'?: React.CSSProperties
    'heading-8'?: React.CSSProperties
    'ui-1'?: React.CSSProperties
    'ui-2'?: React.CSSProperties
    'ui-3'?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    'body-1': true
    'body-2': true
    'body-3': true
    'body-4': true
    'body-5': true
    'body-6': true
    'body-7': true
    'body-8': true
    'body-9': true
    'body-10': true
    'body-11': true
    'body-12': true
    'heading-1': true
    'heading-2': true
    'heading-3': true
    'heading-4': true
    'heading-5': true
    'heading-6': true
    'heading-7': true
    'heading-8': true
    'ui-1': true
    'ui-2': true
    'ui-3': true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    // Add our custom colors
    'transparency-0': true
    'transparency-100': true
    'transparency-200': true
    'transparency-300': true
    'transparency-400': true
    'transparency-500': true
    'transparency-600': true
    'transparency-700': true
    'transparency-800': true
    'transparency-900': true

    'sage-50': true
    'sage-100': true
    'sage-200': true
    'sage-300': true
    'sage-400': true
    'sage-500': true
    'sage-600': true
    'sage-700': true
    'sage-800': true
    'sage-900': true

    'accent-100': true
    'accent-200': true
    'accent-300': true
    'accent-400': true
    'accent-500': true
    'accent-600': true
    'accent-700': true
    'accent-800': true
    'accent-900': true

    'green-100': true
    'green-200': true
    'green-300': true
    'green-400': true
    'green-500': true
    'green-600': true
    'green-700': true
    'green-800': true
    'green-900': true

    'orange-100': true
    'orange-200': true
    'orange-300': true
    'orange-400': true
    'orange-500': true
    'orange-600': true
    'orange-700': true
    'orange-800': true
    'orange-900': true

    'red-100': true
    'red-200': true
    'red-300': true
    'red-400': true
    'red-500': true
    'red-600': true
    'red-700': true
    'red-800': true
    'red-900': true
  }
}

const theme = createTheme({
  palette: {
    ...paletteOptions
  },
  borders: {
    ...borderOptions
  },
  space: {
    ...spacingOptions
  },
  typography: {
    ...typographyOptions
  },
  components: {
    ...customComponentOptions
  }
})

export default theme
